import type { VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cva } from "class-variance-authority";
import NextLink, { type LinkProps as NextLinkProps } from "next/link";
import { cn } from "../utils/classnames";

const linkVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-lg font-jakarta text-base ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    compoundVariants: [
      {
        class: "h-8 p-0",
        size: "primary",
        variant: "footer",
      },
      {
        class: "h-6 p-0",
        size: "primary",
        variant: "footer-legal",
      },
    ],
    defaultVariants: {
      size: "primary",
      variant: "primary",
    },
    variants: {
      size: {
        icon: "h-10 w-10",
        inline: "h-fit p-0",
        lg: "h-11 rounded-full px-8",
        primary: "h-12 px-4 py-2",
        sm: "h-9 rounded-md px-3",
      },
      variant: {
        footer: `text-sm text-wrap underline hover:no-underline rounded-none`,
        "footer-legal": `text-xs leading-6 hover:underline`,
        friendly: `bg-friendly-base text-new-beginnings-base
          hover:bg-friendly-tints-800 border-2 border-friendly-base
          focus:bg-friendly-tints-800`,
        holiday: `justify-start underline hover:no-underline rounded-none h-fit p-0 text-holiday hover:text-holiday`,
        "outline-dark": `bg-new-beginnings-base text-white border-2 border-white
          hover:text-new-beginnings-base hover:bg-new-beginnings-tints-800 hover:border-2 hover:border-white
          focus:text-new-beginnings-base focus:bg-white focus:border-2 focus:border-new-beginnings-base focus:outline focus:outline-2 focus:outline-white`,
        "outline-light": `border-2 border-white text-white bg-transparent
          hover:bg-white hover:text-new-beginnings-base hover:shadow-primary-button
          focus:outline focus:outline-2 focus:border-2 focus:border-transparent focus:outline-white`,
        plain: `justify-start underline hover:no-underline rounded-none h-fit p-0`,
        primary: `bg-new-beginnings-base text-support-base 
          hover:bg-new-beginnings-tints-800 hover:text-new-beginnings-base hover:shadow-primary-button
          hover:ring hover:ring-2 hover:ring-new-beginnings-base
          focus:outline focus:outline-2 focus:border-2 focus:border-support-base focus:outline-new-beginnings-base`,
        secondary: `border-2 border-new-beginnings-base bg-background text-new-beginnings-base hover:bg-new-beginnings-tints-800 hover:shadow-primary-button focus:border-2 focus:border-white focus:outline focus:outline-2 focus:outline-new-beginnings-base 
          hidden h-12 py-2 font-semibold md:inline-flex`,
      },
    },
  },
);

export interface LinkProps
  extends NextLinkProps,
    VariantProps<typeof linkVariants> {
  className?: string;
  children?: React.ReactNode;
  target?: "_blank" | "_self" | "_parent" | "_top";
}

export type LinkVariant = LinkProps["variant"];

const Link = ({ className, variant, size, children, ...props }: LinkProps) => {
  return (
    <NextLink
      className={cn(linkVariants({ className, size, variant }))}
      {...props}
    >
      {children}
    </NextLink>
  );
};

Link.displayName = "Link";

export { Link, linkVariants };
